// WARNING this component is not SSR friendly
import React, { Component } from 'react';
import { bool, func, string, shape, number } from 'prop-types';
import debounce from 'lodash/debounce';
import compose from 'lodash/flowRight';
import classnames from 'classnames';
import { connectSearchBox } from 'react-instantsearch-core';
import { withFeatureFlags, withLocalStorage } from 'connectors';
import { locationShape, localStorageShape, mobileMenuShape } from 'shapes';
import { withGTM, LoadingProgressTransition, withMobileMenu } from 'components';
import { hasLocationChanged } from 'utils';
import Proposals from './Proposals/Proposals';
import './InstantSearchBar.scss';

let searchbar;

class InstantSearchBar extends Component {
    static propTypes = {
        currentRefinement: string.isRequired,
        className: string,
        disableProposals: bool.isRequired,
        refine: func.isRequired,
        onFocus: func.isRequired,
        onClose: func.isRequired,
        onBlur: func.isRequired,
        location: locationShape.isRequired,
        category: shape({
            id: number,
            name: string,
            level: string,
        }),
        removeCategory: func.isRequired,
        pushToGTM: func.isRequired,
        mobileWidth: number.isRequired,
        minLengthWord: number.isRequired,
        maxEntry: number.isRequired,
        enableInstantSearchMobile: bool.isRequired,
        timeDebounceSearchBar: number.isRequired,
        dynamicFilters: bool.isRequired,
        localStorage: localStorageShape.isRequired,
        showProgressBar: bool.isRequired,
        timeToActiveEaseIn: number.isRequired,
        mobileMenu: mobileMenuShape.isRequired,
    };

    static defaultProps = {
        className: null,
        category: null,
    };

    static getDerivedStateFromProps(props, state) {
        if (props.currentRefinement !== state.currentRefinement) {
            if (
                props.currentRefinement.length === 0 &&
                props.currentRefinement.length < state.currentRefinement.length
            ) {
                window.scroll(0, 0);
            }
            return {
                currentRefinement: props.currentRefinement,
            };
        }
        return null;
    }

    constructor(props) {
        super(props);

        this.state = {
            closing: false,
            canShowProposals: false,
            currentRefinement: props.currentRefinement, // eslint-disable-line react/no-unused-state
        };

        this.isMobile = window.matchMedia(
            `(max-width: ${props.mobileWidth}px)`,
        ).matches;
        this.keyCodes = { enter: 13, escape: 27 };

        this.allTextSelected = false;
        this.shouldSaveInHistory = false;

        searchbar = document.getElementById('searchbar');
        searchbar.value = this.state.currentRefinement;
        searchbar.addEventListener('focus', this.handleSearchbarFocus);
        searchbar.addEventListener('blur', props.onBlur);
        window.addEventListener('select', this.handleSearchbarSelection);
        window.addEventListener('scroll', this.saveSearchHistory, {
            passive: true,
        });
        searchbar.addEventListener('input', this.handleInputChange);
        searchbar.addEventListener('focusout', this.saveSearchHistory);
        searchbar.addEventListener('keyup', this.handleSearchbarKeyUp);

        this.clearButton = document.querySelector('.searchbar-basic__clear');
        this.clearButton.addEventListener('click', this.handleClearButtonClick);

        this.proposals = document.getElementById('instantsearch-portal-header');

        this.grid = document.querySelector('.header > .header__container');
        this.grid.classList.add('algolia-grid');
        this.grid.addEventListener('click', this.handleClickHeader);

        this.historyMinLength = props.minLengthWord;
        this.nbMaxhistoryEntry = props.maxEntry;
    }

    componentDidMount() {
        if (searchbar.value) {
            // display the clear icon if there is a value when we init the search
            const basicInputClear = document.getElementById(
                'searchbar-basic-clear',
            );
            basicInputClear.style.display = 'block';
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const {
            className,
            currentRefinement,
            location,
            disableProposals,
        } = this.props;
        const {
            currentRefinement: currentRefinementState,
            closing,
            canShowProposals,
        } = this.state;
        // we ignore category and isSearchStalled
        return (
            className !== nextProps.className ||
            currentRefinement !== nextProps.currentRefinement ||
            hasLocationChanged(nextProps.location, location) ||
            disableProposals !== nextProps.disableProposals ||
            canShowProposals !== nextState.canShowProposals ||
            closing !== nextState.closing ||
            currentRefinementState !== nextState.currentRefinement
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.props.location.fromBrowserBack &&
            prevState.currentRefinement !== this.state.currentRefinement
        ) {
            searchbar.value = this.state.currentRefinement;
            const basicInputClear = document.getElementById(
                'searchbar-basic-clear',
            );
            basicInputClear.style.display = 'block';
        }
    }

    componentWillUnmount() {
        searchbar.removeEventListener('focus', this.handleSearchbarFocus);
        searchbar.removeEventListener('focus', this.props.onBlur);
        window.removeEventListener('select', this.handleSearchbarSelection);
        window.removeEventListener('scroll', this.saveSearchHistory);
        searchbar.removeEventListener('input', this.handleInputChange);
        searchbar.removeEventListener('focusout', this.handleSearchbarBlur);
        searchbar.removeEventListener('keyup', this.handleSearchbarKeyUp);
        this.clearButton.removeEventListener(
            'click',
            this.handleClearButtonClick,
        );
        this.grid.removeEventListener('click', this.handleClickHeader);
    }

    handleClickHeader = event => {
        this.allTextSelected = false;
        if (
            event.target !== searchbar &&
            event.target !== this.clearButton &&
            !this.proposals.contains(event.target)
        ) {
            this.props.onClose();
        }
    };

    handleSearchbarFocus = () => {
        const { location = { params: {} }, onFocus, mobileMenu } = this.props;
        this.allTextSelected = false;
        onFocus(true);
        if (!location?.params || location?.params.query.length === 0) {
            this.setState({ canShowProposals: true });
        }

        const menu = document.getElementsByClassName(
            'js-menu-mobile__content',
        )[0];
        if (menu && menu.classList.contains('js-menu-mobile--active')) {
            mobileMenu.closeMobileMenu(false);
        }
    };

    handleSearchbarBlur = evt => {
        this.saveSearchHistory(evt);
        this.setState({ canShowProposals: false });
    };

    handleSearchbarSelection = () => {
        this.allTextSelected = false;
        if (searchbar && document.activeElement === searchbar) {
            if (
                searchbar.selectionStart === 0 &&
                searchbar.selectionEnd === searchbar.value.length
            ) {
                this.allTextSelected = true;
            }
        }
    };

    handleSearchbarKeyUp = evt => {
        // if mobile user press "enter/search" button we want hide keyboard
        if (evt.keyCode === this.keyCodes.enter) {
            searchbar.blur();
            this.props.refine(searchbar.value);
        } else if (evt.keyCode === this.keyCodes.escape) {
            this.props.onClose(evt);
        }
    };

    saveSearchHistory = () => {
        const { currentRefinement } = this.state;
        const { category = {} } = this.props;

        if (
            !this.shouldSaveInHistory ||
            currentRefinement.length < this.historyMinLength
        )
            return;

        this.props.localStorage.get().then(value => {
            const history = value || [];
            const searchValueIndex = history.indexOf(currentRefinement);

            this.props.pushToGTM({
                event: 'search',
                query: currentRefinement,
                category_id: category && category.id,
                category_name: category && category.name,
                category_level: category && category.level,
            });

            if (
                searchValueIndex !== -1 ||
                history.length >= this.nbMaxhistoryEntry
            ) {
                history.splice(searchValueIndex, 1);
            }

            history.unshift(currentRefinement);
            this.props.localStorage.set(history);

            this.shouldSaveInHistory = false;
        });
    };

    handleInputChange = evt => {
        const { removeCategory, showProgressBar } = this.props;
        this.shouldSaveInHistory = true;

        // stop event if value is empty (fix IE11)
        if (evt.target.value === '') {
            if (!this.state.canShowProposals) {
                this.setState({ canShowProposals: true });
            }
            this.handleClearButtonClick();
            return;
        }
        if (this.state.canShowProposals) {
            this.setState({ canShowProposals: false });
        }

        if (this.allTextSelected) {
            removeCategory();
            this.allTextSelected = false;
        }

        this.clearButton.style.display =
            evt.target.value.length > 0 ? 'block' : 'none';
        if (!this.isMobile || this.props.enableInstantSearchMobile) {
            this.debouncedRefine(evt.target.value);
            // if (evt.target.value.length <= 5) {
            //     this.throttledRefine(evt.target.value);
            // } else {
            //     this.throttledRefine.cancel();
            //     this.debouncedRefine(evt.target.value);
            // }

            if (showProgressBar) {
                // <TO DELETE>
                // Start
                this.setState({
                    start: true,
                    finish: false,
                });
                // Finishing
                setTimeout(() => {
                    this.setState({
                        start: false,
                        finish: true,
                    });
                }, 300);
                // Back to normal
                setTimeout(() => {
                    this.setState({
                        start: false,
                        finish: false,
                    });
                }, 400);
                // </TO DELETE>
            }
        }
    };

    // eslint-disable-next-line react/sort-comp
    debouncedRefine = debounce(value => {
        this.props.refine(value);
    }, this.props.timeDebounceSearchBar);

    handleClearButtonClick = () => {
        this.debouncedRefine.cancel();
        this.clearButton.style.display = 'none';
        this.allTextSelected = false;
        searchbar.value = '';
        this.props.refine('');
        setTimeout(() => {
            // delay the category reset to avoid conflict with the refine
            this.props.removeCategory();
        }, 10);
        searchbar.focus();
        window.scroll(0, 0);
        this.setState({
            canShowProposals: true,
        });
    };

    handleTrendSelected = trend => {
        if (this.props.dynamicFilters) {
            this.clearButton.style.display =
                trend.name.length > 0 ? 'block' : 'none';
            searchbar.value = trend.name;
            this.props.refine(trend.name);
            this.setState({
                canShowProposals: false,
            });
        } else {
            window.location.href = trend.url;
        }
    };

    handleClose = () => {
        this.setState({ closing: true });
    };

    handleAnimationEnd = () => {
        if (this.state.closing) {
            this.setState({ closing: false });
        }
    };

    handleHistorySelected = value => {
        this.clearButton.style.display = value.length > 0 ? 'block' : 'none';
        searchbar.value = value;
        this.props.refine(value);
        this.setState({
            canShowProposals: false,
        });
    };

    render() {
        const {
            className,
            disableProposals,
            timeToActiveEaseIn,
            showProgressBar,
        } = this.props;
        const { canShowProposals } = this.state;

        const rootClassnames = classnames('searchBar', className, {
            searchBar_closing: this.state.closing,
        });

        const proposalsClassnames = classnames('searchBar__proposals');

        return (
            <div
                className={rootClassnames}
                onAnimationEnd={this.handleAnimationEnd}
            >
                {showProgressBar && (
                    <LoadingProgressTransition
                        starting={this.state.start}
                        finishing={this.state.finish}
                        timeToActiveEaseIn={timeToActiveEaseIn}
                    />
                )}
                {canShowProposals && !disableProposals ? (
                    <div className="searchBar__banner">
                        <Proposals
                            className={proposalsClassnames}
                            onTrendSelected={this.handleTrendSelected}
                            onHistorySelected={this.handleHistorySelected}
                            closeProposals={this.props.onClose}
                        />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default compose(
    withFeatureFlags({
        mobileWidth: 'instantSearch.mobileWidth',
        minLengthWord: 'instantSearch.history.minLengthWord',
        maxEntry: 'instantSearch.history.maxEntry',
        enableInstantSearchMobile: 'instantSearch.enableInstantSearchMobile',
        timeDebounceSearchBar: 'instantSearch.timeDebounceSearchBar',
        dynamicFilters: 'instantSearch.dynamicFilters',
        showProgressBar: 'instantSearch.progressBar',
        timeToActiveEaseIn:
            'instantSearch.progressBar.timeToActiveEaseInSearchBar',
    }),
    connectSearchBox,
    withLocalStorage('searchHistory'),
    withGTM,
    withMobileMenu,
)(InstantSearchBar);
